/**
 * Rich-text utilities.
 *
 * We store LMS content (course/module/lesson descriptions + rich content
 * sections) as HTML from the contentEditable editor. Anywhere we render
 * that value as **plain text** (card previews, meta descriptions, list
 * subtitles, share cards) we need to strip tags first, or the raw
 * `<p>…</p>` markup shows up in the UI.
 */

/** Strip HTML tags for plain-text previews (card subtitles, meta, etc.). */
export const stripHtml = (input) => {
  if (input == null) return '';
  const s = String(input);
  if (!s) return '';
  if (typeof document === 'undefined') {
    // SSR / edge — fall back to a regex strip
    return s.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const el = document.createElement('div');
  el.innerHTML = s;
  return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim();
};

/** Truncate helper for previews. */
export const stripAndTruncate = (input, max = 160) => {
  const s = stripHtml(input);
  return s.length > max ? s.slice(0, max - 1).trimEnd() + '…' : s;
};

/**
 * Lightweight markdown → HTML converter for lesson content sections.
 *
 * We deliberately DON'T pull in `marked` or `markdown-it` (both ~40 KB
 * gzipped) because we only need the handful of primitives the LMS AI
 * generator emits: `##`/`###` headings, `**bold**`, `*italic*`,
 * `[cite:xxx]` markers, unordered `-` lists, numbered `1.` lists,
 * fenced code blocks, links, and paragraphs.
 *
 * Preserves inline `[cite:doc-id]` markers untouched so the caller's
 * `transformCiteMarkers` pass can turn them into numbered pills.
 */
export const mdToHtml = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';

  // 1) Escape any raw HTML the LLM emitted so it renders as text —
  //    we control the tag set ourselves.
  const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // 2) Pull out fenced code blocks first so their contents aren't
  //    munged by heading/list regexes.
  const codeBlocks = [];
  let src = s.replace(/```([\s\S]*?)```/g, (_m, code) => {
    codeBlocks.push(`<pre class="bg-slate-900 text-slate-100 rounded p-3 my-3 overflow-x-auto"><code>${esc(code.trim())}</code></pre>`);
    return `\u0000CODE${codeBlocks.length - 1}\u0000`;
  });

  // 3) Split into paragraph-ish blocks separated by blank lines.
  const blocks = src.split(/\n{2,}/);
  const htmlBlocks = blocks.map((block) => {
    const trimmed = block.trim();
    if (!trimmed) return '';
    // Passthrough for extracted code blocks
    if (/^\u0000CODE\d+\u0000$/.test(trimmed)) return trimmed;

    // Headings
    let m = /^(#{2,4})\s+(.+)$/m.exec(trimmed);
    if (m && trimmed.split('\n').length === 1) {
      const level = m[1].length;
      return `<h${level}>${inline(esc(m[2]))}</h${level}>`;
    }
    // Unordered list
    if (/^[-*]\s+/.test(trimmed)) {
      const items = trimmed
        .split(/\n/)
        .filter((l) => /^\s*[-*]\s+/.test(l))
        .map((l) => `<li>${inline(esc(l.replace(/^\s*[-*]\s+/, '')))}</li>`)
        .join('');
      return `<ul>${items}</ul>`;
    }
    // Ordered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items = trimmed
        .split(/\n/)
        .filter((l) => /^\s*\d+\.\s+/.test(l))
        .map((l) => `<li>${inline(esc(l.replace(/^\s*\d+\.\s+/, '')))}</li>`)
        .join('');
      return `<ol>${items}</ol>`;
    }
    // Blockquote
    if (/^>\s+/.test(trimmed)) {
      const inner = trimmed.split(/\n/).map((l) => l.replace(/^>\s?/, '')).join('<br/>');
      return `<blockquote>${inline(esc(inner))}</blockquote>`;
    }
    // Default paragraph — preserve single-line breaks as <br/>
    const withBreaks = trimmed.split(/\n/).map((l) => inline(esc(l))).join('<br/>');
    return `<p>${withBreaks}</p>`;
  });

  let html = htmlBlocks.join('\n');
  // Restore fenced code blocks
  html = html.replace(/\u0000CODE(\d+)\u0000/g, (_m, i) => codeBlocks[Number(i)] || '');
  return html;
};

// Inline formatting — runs AFTER esc(), so **/</> in the source is
// already safe. `[cite:xxx]` markers are preserved untouched for the
// caller's transformCiteMarkers pass.
const inline = (t) =>
  t
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|\W)\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\((https?:[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary underline underline-offset-2">$1</a>');

